/*
  Полоса портфеля под плашками первого экрана — §3.1.3, §4.1.2.

  🔴 Выбрана владельцем 29.09.2026 из четырёх вариантов компактного блока
  (макеты - `макеты-графиков-дашборда` на рабочем столе). Полоса сроков и
  календарь сгорания были на выбор вместе с ней и уехали на «Отчёты»: первому
  экрану довольно одного ответа, а у отчётов есть период, которого здесь нет.

  Зачем она, если те же цифры уже стоят в плашках. Плашка отвечает «сколько»,
  полоса - «сколько это в сравнении с остальным». Четыре суммы, набранные
  крупно, глазом не складываются в целое: «20,0 млн» и «840 тыс» читаются как
  два числа одного порядка, пока не увидишь их одной шкалой.

  🔴 Полоса НЕ считает ничего своего. Сегменты - те же величины, что в плашках,
  и ведут они в тот же отбор реестра. Своя арифметика здесь означала бы третье
  число об одном и том же - после того, как дашборд уже однажды разошёлся сам с
  собой в плашке «В работе».
*/

import { Link } from 'react-router-dom'

import { accounts as accountsWord, money } from '../api/format.js'

/*
  Доля считается от суммы показанных сегментов, а не от «всего портфеля»,
  присланного отдельным числом. Так доли по определению складываются в сто
  процентов: величина, не попавшая в полосу, не может тихо уменьшить каждую
  долю. Полноту разбиения по маршрутам стережёт тест на сервере
  (`test_dashboard_routes.py`).
*/
function share(amount, total) {
  if (!total) return '0%'
  return `${((Number(amount) / total) * 100).toFixed(1).replace('.', ',')}%`
}

/*
  🔴 Ширина сегмента и подпись к нему - РАЗНЫЕ строки, и это не педантизм.
  Русская запись доли идёт через запятую, а CSS понимает только точку: ширина
  «94,4%» для него не число вовсе, и сегмент схлопывается до своей минимальной
  ширины. Тесты этого не видят - они читают подписи, - и полоса из четырёх
  сегментов превращается в двадцать пикселей слева. Найдено осмотром в браузере
  29.09.2026, через минуту после зелёного прогона.
*/
function width(amount, total) {
  if (!total) return '0%'
  return `${((Number(amount) / total) * 100).toFixed(4)}%`
}

/*
  Заголовок и пояснение приходят пропсами с 29.09.2026: та же полоса рисует на
  «Отчётах» ещё и сроки давности («Сколько осталось времени»), а арифметика,
  оформление и поведение у них общие. Второй компонент ради другого заголовка
  означал бы две копии, которые разъедутся при первой правке ширины сегмента.
*/
export default function PortfolioBar({ segments, title, note }) {
  // Пустой сегмент не рисуется и не занимает строки легенды: ноль рублей по
  // маршруту означает, что таких счетов нет вовсе, а не что они важны.
  const shown = segments.filter((segment) => Number(segment.amount) > 0)
  const total = shown.reduce((sum, segment) => sum + Number(segment.amount), 0)
  if (!total) return null

  return (
    <section className="card portfolio">
      <h2 className="section__title">{title}</h2>

      {/* Полоса скрыта от читалки экрана: то же самое написано словами в
          легенде ниже, и читать одно и то же дважды незачем. */}
      <div className="portfolio__bar" aria-hidden="true">
        {shown.map((segment) => (
          <Link
            key={segment.title}
            to={segment.to}
            className={`portfolio__piece portfolio__piece--${segment.tone}`}
            style={{ width: width(segment.amount, total) }}
            /* Подсказка мышью: у сегмента нет подписи внутри - на долю в
               полпроцента она не помещается, - а навести на него человек
               пробует первым делом. */
            title={`${segment.title}: ${money(segment.amount)} - ${share(segment.amount, total)}`}
            /* Из обхода с клавиатуры сегмент исключён: та же ссылка стоит
               строкой легенды ниже, и проходить один и тот же переход дважды
               незачем. Заодно это снимает запрет на фокус внутри `aria-hidden`. */
            tabIndex={-1}
          />
        ))}
      </div>

      <div className="portfolio__legend">
        {shown.map((segment) => (
          <Link key={segment.title} to={segment.to} className="portfolio__row">
            <span className={`portfolio__mark portfolio__mark--${segment.tone}`} />
            <span className="portfolio__name">{segment.title}</span>
            <span className="portfolio__money">{money(segment.amount)}</span>
            <span className="portfolio__share">
              {share(segment.amount, total)} · {accountsWord(segment.accounts)}
            </span>
          </Link>
        ))}
      </div>

      {note ? <p className="chart__why">{note}</p> : null}
    </section>
  )
}
